import { Body2, Label, LinkButton } from '@cian/ui-kit';
import { IconActionClose16 } from '@cian/ui-kit-design-tokens/icons';

import { type FC, type JSX, useMemo } from 'react';

import { type RoomsInfoItem, RoomsList } from 'shared/components/RoomsList';
import { Transport } from 'shared/components/Transport';
import { WrapElementsSpacer } from 'shared/components/WrapElementsSpacer';
import { type RemotenessTypes } from 'shared/types/RemotenessTypes';
import { type TransportTypes } from 'shared/types/TransportTypes';
import { extractUndefined } from 'shared/utils/extractUndefined';
import { splitStringToMaxCharsInRow } from 'shared/utils/splitStringToMaxCharsInRow';

import styles from './MapBalloon.css';

type TransportType = {
  id: number;
  name: string;
  iconColor?: string | null;
  remoteness?: string | null;
  remotenessType?: RemotenessTypes | null;
  iconType?: TransportTypes | null;
};

type MapBalloonProps = {
  title: string;
  subtitle: string | null;
  transports: TransportType[];
  imageUrl: string;
  roomsInfo: RoomsInfoItem[];
  promoLabel: string | null;
  url: string;
  isSeriaPlus: boolean;
  newbuildingId: number;
};

export const MapBalloon: FC<MapBalloonProps> = ({
  title,
  subtitle,
  transports,
  imageUrl,
  roomsInfo,
  promoLabel,
  url,
  isSeriaPlus,
  newbuildingId,
}) => {
  const splitTitleViews = useMemo<Array<string | JSX.Element> | string>(() => {
    if (!isSeriaPlus) {
      return title;
    }

    const splitTitle = splitStringToMaxCharsInRow(title, 22);
    const lastSplitTitleIndex = splitTitle.length - 1;

    const result = Array.of<string | JSX.Element>();

    for (let index = 0; index < lastSplitTitleIndex; index += 1) {
      result.push(splitTitle[index], <br key={`br_${index}`} />);
    }

    result.push(
      <WrapElementsSpacer horizontalSpace={8} key={lastSplitTitleIndex + 1} verticalSpace={4}>
        {splitTitle[lastSplitTitleIndex]}
        <Label background="theme-gray" color="text-inverted-default" data-testid="seria-plus-label">
          Серия плюс
        </Label>
      </WrapElementsSpacer>,
    );

    return result;
  }, [isSeriaPlus, title]);

  return (
    <article className={styles['container']} data-testid="map-balloon">
      <span className={styles['close']} data-testid="close-button" id="close">
        <IconActionClose16 color="icon-inverted-default" data-testid="close-icon" />
      </span>
      <div className={styles['image-wrapper']} data-testid="image-wrapper">
        {!!promoLabel && (
          <div className={styles['promo-label-wrapper']}>
            <Label background="control-inverted-primary-default" color="#fc4c02" data-testid="promo-label">
              {promoLabel}
            </Label>
          </div>
        )}
        <div className={styles['image-wrapper-inner']}>
          <img alt={title} data-testid="image" height={200} src={imageUrl} width={355} />
        </div>
      </div>
      <div className={styles['content']} data-testid="content">
        <div className={styles['title']} data-testid="title">
          {splitTitleViews}
        </div>
        {!!subtitle && (
          <Body2 color="text-secondary-default" data-testid="subtitle">
            {subtitle}
          </Body2>
        )}

        {!!transports.length && (
          <div className={styles['transports-wrapper']} data-testid="transports-wrapper">
            {transports.map(transport => (
              <Transport
                iconColor={extractUndefined(transport.iconColor)}
                iconType={extractUndefined(transport.iconType)}
                key={transport.id}
                name={transport.name}
                remoteness={extractUndefined(transport.remoteness)}
                remotenessIconColor="icon-secondary-default"
                remotenessType={extractUndefined(transport.remotenessType)}
                textColor="text-secondary-default"
              />
            ))}
          </div>
        )}

        {!!roomsInfo.length && (
          <div className={styles['rooms-info-wrapper']} data-testid="rooms-list-wrapper">
            <RoomsList list={roomsInfo} maxItems={6} />
          </div>
        )}

        <div className={styles['link-button-wrapper']} data-testid="link-button-wrapper">
          <LinkButton
            fullWidth
            data-newbuilding-id={String(newbuildingId)}
            data-pik-map-balloon-cta=""
            data-testid="link-button"
            href={url}
            rel="noopener noreferrer"
            size="M"
            target="_blank"
            theme="fill_secondary"
          >
            Смотреть проект
          </LinkButton>
        </div>
      </div>
    </article>
  );
};
